import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0TableOfContent, TOCItem } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { MyCommunitiesDocument, OtherCommunitiesDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'

import styles from './styles.module.css'

type Group = {
  id: number
  title: string
  description?: string | null
  isMember: boolean
}

const CommunitiesSidebar = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const {
    nodes: myCommunitiesNodes,
    data: myCommunitiesData,
    controls: {
      fetchMore: fetchMoreMyCommunities,
      hasMore: hasMoreMyCommunities,
      loading: loadingMoreMyCommunities,
    },
  } = usePaginatedQuery(MyCommunitiesDocument, {
    extractConnection: (data) =>
      data.posts?.groupsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
  })

  const {
    nodes: otherCommunitiesNodes,
    controls: {
      fetchMore: fetchMoreOtherCommunities,
      hasMore: hasMoreOtherCommunities,
      loading: loadingMoreOtherCommunities,
    },
  } = usePaginatedQuery(OtherCommunitiesDocument, {
    extractConnection: (data) =>
      data.posts?.groupsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
  })

  useUnsafeEffect(() => {
    if (hasMoreMyCommunities && !loadingMoreMyCommunities) {
      fetchMoreMyCommunities()
    }
  }, [hasMoreMyCommunities, loadingMoreMyCommunities, fetchMoreMyCommunities])

  useUnsafeEffect(() => {
    if (hasMoreOtherCommunities && !loadingMoreOtherCommunities) {
      fetchMoreOtherCommunities()
    }
  }, [hasMoreOtherCommunities, loadingMoreOtherCommunities, fetchMoreOtherCommunities])

  const myCommunities = useMemo<Group[]>(() => {
    return myCommunitiesNodes
  }, [myCommunitiesNodes])

  const otherCommunities = useMemo<Group[]>(() => {
    return otherCommunitiesNodes
  }, [otherCommunitiesNodes])

  const canCreateGroup = useMemo(() => {
    return myCommunitiesData?.posts?._policies?.groupCreate?.can ?? false
  }, [myCommunitiesData])

  const selectedGroupId = id ? Number(id) : undefined
  const isFeedSelected = !id
  const activeItemId = isFeedSelected ? 'feed' : selectedGroupId?.toString()

  const handleGroupClick = useCallback(
    (groupId: number) => {
      navigate(navigation.communities.id.fullPath({ id: groupId.toString() }))
    },
    [navigate, navigation]
  )

  const handleFeedClick = useCallback(() => {
    navigate(navigation.communities.fullPath())
  }, [navigate, navigation])

  const handleAddCommunityClick = useCallback(() => {
    if (id) {
      navigate(navigation.communities.id.new.fullPath({ id }))
    } else {
      navigate(navigation.communities.new.fullPath())
    }
  }, [navigate, navigation, id])

  const tocItems = useMemo(() => {
    const items: TOCItem[] = [
      {
        id: 'feed',
        label: i18n.t('posts.communities.feed'),
        onClick: handleFeedClick,
      },
    ]

    if (myCommunities.length > 0) {
      items.push({
        id: 'my-communities',
        label: i18n.t('posts.communities.my_communities'),
        children: myCommunities.map((group) => ({
          id: group.id.toString(),
          label: group.title ?? '',
          onClick: () => handleGroupClick(group.id),
        })),
      })
    }

    if (otherCommunities.length > 0) {
      items.push({
        id: 'other-communities',
        label: i18n.t('posts.communities.other_communities'),
        children: otherCommunities.map((group) => ({
          id: group.id.toString(),
          label: group.title ?? '',
          onClick: () => handleGroupClick(group.id),
        })),
      })
    }

    return items
  }, [myCommunities, otherCommunities, handleGroupClick, handleFeedClick])

  return (
    <Box
      border={{ right: { style: 'solid', width: 's1', color: 'grey200' } }}
      flexDirection="column"
      height="full"
      style={{ minHeight: '0' }}
    >
      <Box
        className={styles.sidebarWrapper}
        style={{ minHeight: '0', flexGrow: '1' }}
      >
        <F0TableOfContent
          collapsible
          key={JSON.stringify([
            ...myCommunities.map((g) => g.id),
            ...otherCommunities.map((g) => g.id),
          ])}
          activeItem={activeItemId}
          items={tocItems}
          showSearchBox
          searchPlaceholder={i18n.t('posts.communities.communities_search_placeholder')}
          className={styles.sidebar}
        />
        {canCreateGroup && (
          <Box padding="s16" alignItems="flexStart" className={styles.createButton}>
            <F0Button
              variant="outline"
              size="md"
              onClick={handleAddCommunityClick}
              icon={Plus}
              label={i18n.t('dashboard.groups.create_group')}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CommunitiesSidebar
